import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';

const ProviderPricingPage = () => {
  const navigate = useNavigate();
  const { user, sessionToken } = useUnifiedAuth();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [highlightedTiers, setHighlightedTiers] = useState(['Free']); // Start with Free highlighted
  
  // Launch promotion ended
  const LAUNCH_PROMOTION = {
    active: false,
    discount: 0.50, // 50% off
    duration: '6 months',
    limit: '100 providers',
    tiers: ['Basic', 'Pro'] // Only Basic and Pro get discount
  };
  
  const getPrice = (tier, originalPrice) => {
    if (LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tier)) {
      return originalPrice * (1 - LAUNCH_PROMOTION.discount);
    }
    return originalPrice;
  };
  
  const hasPromotion = (tier) => {
    return LAUNCH_PROMOTION.active && LAUNCH_PROMOTION.tiers.includes(tier);
  };

  const handlePlanSelect = (planName, tierId) => {
    setSelectedPlan(planName);
    
    if (planName === 'Enterprise') {
      // For enterprise, show contact modal
      alert('Thank you for your interest in our Enterprise plan! \n\nEnterprise is custom priced for hospitals and large chains, with a 5% commission.\n\nFor a quote:\nEmail: enterprise@enayahub.net\nPhone: +965 6552 7758');
      return;
    }

    // Check if user is logged in as provider
    if (!user || user.user_type !== 'provider') {
      // Not logged in - navigate to registration with plan pre-selected
      navigate('/register', { 
        state: { 
          userType: 'provider', 
          selectedPlan: planName,
          billingCycle: billingCycle
        } 
      });
    } else {
      // Already logged in as provider - navigate to subscription management page
      navigate('/subscription', {
        state: {
          selectedTierId: tierId,
          billingCycle: billingCycle
        }
      });
    }
  };

  return (
    <section className="bg-slate-950 min-h-screen py-16 px-4 sm:px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        {/* Launch Promotion Banner */}
        {LAUNCH_PROMOTION.active && (
          <div className="mb-8 bg-gradient-to-r from-pink-600 via-purple-600 to-indigo-600 rounded-lg p-6 text-center border-2 border-yellow-400 shadow-2xl animate-pulse">
            <div className="flex items-center justify-center gap-3 mb-2">
              <span className="text-4xl">🎉</span>
              <h3 className="text-2xl md:text-3xl font-black text-white">LAUNCH PROMOTION</h3>
              <span className="text-4xl">🎉</span>
            </div>
            <p className="text-xl md:text-2xl font-bold text-yellow-300 mb-2">
              50% OFF Basic & Pro Plans for 6 Months!
            </p>
            <p className="text-white text-sm md:text-base">
              Limited to the first 100 providers - Don't miss out! 🚀
            </p>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white">Provider Subscription Plans</h2>
          <p className="mt-3 text-slate-300">
            Flexible plans for clinics, salons, spas, and medical centers in Kuwait. All plans include booking, bookkeeping, and free reminders.
          </p>
        </div>

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-slate-900 rounded-lg p-1 inline-flex border border-slate-800">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-6 py-2 rounded-lg font-medium transition-colors relative ${
                billingCycle === 'annual'
                  ? 'bg-sky-500 text-white'
                  : 'text-slate-300 hover:text-white'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                2 months free
              </span>
            </button>
          </div>
        </div>

        {/* Cards (mobile / highlight) */}
        <div className="grid gap-6 md:grid-cols-4 mb-10">
          {/* Free */}
          <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-6 flex flex-col">
            <h3 className="text-lg font-semibold text-white">Free</h3>
            <p className="mt-2 text-3xl font-bold text-white">0 KWD</p>
            <p className="text-slate-400 text-sm mt-1">Fully functional online booking</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-200">
              <li>• 15% commission</li>
              <li>• 5 staff members</li>
              <li>• 35 services</li>
              <li>• 1 branch</li>
              <li>• 3 months scheduling</li>
              <li>• Unlimited bookings</li>
              <li>• Basic marketing</li>
              <li>• Community support</li>
              <li>• Bookkeeping + reminders</li>
            </ul>
            <button 
              onClick={() => handlePlanSelect('Free', 'free-tier-id')}
              className="mt-auto w-full bg-slate-800 text-white py-2 rounded-xl text-sm font-medium hover:bg-slate-700 transition"
            >
              {user && user.user_type === 'provider' ? 'Current Plan' : 'Get Started Free'}
            </button>
          </div>

          {/* Basic */}
          <div className="rounded-2xl bg-slate-900/80 border border-sky-500/60 p-6 flex flex-col relative overflow-hidden">
            {hasPromotion('Basic') ? (
              <span className="absolute right-4 top-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-3 py-1 rounded-full font-bold animate-pulse">
                50% OFF 🎉
              </span>
            ) : (
              <span className="absolute right-4 top-4 bg-sky-500/10 text-sky-200 text-xs px-3 py-1 rounded-full border border-sky-500/30">Popular</span>
            )}
            <h3 className="text-lg font-semibold text-white">Basic</h3>
            <div className="mt-2">
              {hasPromotion('Basic') && (
                <p className="text-lg text-slate-400 line-through">
                  {billingCycle === 'annual' ? '240 KWD' : '20 KWD'}
                </p>
              )}
              <p className="text-3xl font-bold text-white">
                {billingCycle === 'annual' 
                  ? `${getPrice('Basic', 200)} KWD` 
                  : `${getPrice('Basic', 20)} KWD`}
                <span className="text-sm text-slate-300 font-normal"> 
                  / {billingCycle === 'annual' ? 'year' : 'mo'}
                </span>
              </p>
            </div>
            {hasPromotion('Basic') && (
              <p className="text-xs text-pink-400 font-bold mt-1">
                🎁 First {LAUNCH_PROMOTION.duration} - Then regular price
              </p>
            )}
            {billingCycle === 'annual' && !hasPromotion('Basic') && (
              <p className="text-xs text-green-400 font-medium">2 months free — save 40 KWD per year!</p>
            )}
            <p className="text-slate-400 text-sm mt-2">For growing providers and multi-branch salons.</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-200">
              <li>• 12.5% commission</li>
              <li>• 8 staff members</li>
              <li>• 60 services</li>
              <li>• 3 branches</li>
              <li>• 6 months scheduling</li>
              <li>• Unlimited bookings</li>
              <li>• Basic marketing package</li>
              <li>• Email support</li>
              <li>• Bookkeeping + reminders</li>
            </ul>
            <button 
              onClick={() => handlePlanSelect('Basic', 'basic-tier-id')}
              className="mt-auto w-full bg-sky-500 text-slate-950 py-2 rounded-xl text-sm font-medium hover:bg-sky-400 transition"
            >
              {user && user.user_type === 'provider' ? 'Upgrade to Basic' : 'Choose Basic'}
            </button>
          </div>

          {/* Pro */}
          <div className="rounded-2xl bg-slate-900/50 border border-purple-500/40 p-6 flex flex-col relative overflow-hidden">
            {hasPromotion('Pro') && (
              <span className="absolute right-4 top-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white text-xs px-3 py-1 rounded-full font-bold animate-pulse">
                50% OFF 🎉
              </span>
            )}
            <h3 className="text-lg font-semibold text-white">Pro</h3>
            <div className="mt-2">
              {hasPromotion('Pro') && (
                <p className="text-lg text-slate-400 line-through">
                  {billingCycle === 'annual' ? '720 KWD' : '60 KWD'}
                </p>
              )}
              <p className="text-3xl font-bold text-white">
                {billingCycle === 'annual' 
                  ? `${getPrice('Pro', 600)} KWD` 
                  : `${getPrice('Pro', 60)} KWD`}
                <span className="text-sm text-slate-300 font-normal"> 
                  / {billingCycle === 'annual' ? 'year' : 'mo'}
                </span>
              </p>
            </div>
            {hasPromotion('Pro') && (
              <p className="text-xs text-pink-400 font-bold mt-1">
                🎁 First {LAUNCH_PROMOTION.duration} - Then regular price
              </p>
            )}
            {billingCycle === 'annual' && !hasPromotion('Pro') && (
              <p className="text-xs text-green-400 font-medium">2 months free — save 120 KWD per year!</p>
            )}
            <p className="text-slate-400 text-sm mt-2">For clinics and premium brands.</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-200">
              <li>• 10% commission</li>
              <li>• 12 staff members</li>
              <li>• 75 services</li>
              <li>• 6 branches</li>
              <li>• 18 months scheduling</li>
              <li>• Unlimited bookings</li>
              <li>• Pro marketing package</li>
              <li>• Priority support</li>
              <li>• Bookkeeping + reminders</li>
            </ul>
            <button 
              onClick={() => handlePlanSelect('Pro', 'pro-tier-id')}
              className="mt-auto w-full bg-purple-500 text-slate-950 py-2 rounded-xl text-sm font-medium hover:bg-purple-400 transition"
            >
              {user && user.user_type === 'provider' ? 'Upgrade to Pro' : 'Go Pro'}
            </button>
          </div>

          {/* Enterprise */}
          <div className="rounded-2xl bg-slate-900/20 border border-amber-500/50 p-6 flex flex-col relative">
            <span className="absolute right-4 top-4 bg-amber-500/10 text-amber-200 text-xs px-3 py-1 rounded-full border border-amber-500/30">Custom pricing</span>
            <h3 className="text-lg font-semibold text-white">Enterprise</h3>
            <p className="mt-2 text-3xl font-bold text-white">
              Let's talk
            </p>
            <p className="text-sm text-slate-300">Custom quote for your organisation</p>
            <p className="text-slate-400 text-sm mt-2">For hospitals & large chains.</p>
            <ul className="mt-4 space-y-2 text-sm text-slate-200 mb-6">
              <li>• 5% commission</li>
              <li>• 25 – 100 staff members</li>
              <li>• Unlimited services</li>
              <li>• 10 – 20 branches</li>
              <li>• 36 months scheduling</li>
              <li>• Unlimited bookings</li>
              <li>• Ultimate marketing package</li>
              <li>• Dedicated Manager support</li>
              <li>• Doctor–user appointments</li>
              <li>• Bookkeeping + reminders</li>
            </ul>
            <button 
              onClick={() => handlePlanSelect('Enterprise', 'enterprise-tier-id')}
              className="mt-auto w-full bg-amber-500 text-slate-950 py-2 rounded-xl text-sm font-medium hover:bg-amber-400 transition"
            >
              Contact Sales
            </button>
          </div>
        </div>

        {/* Detailed comparison table (desktop) */}
        <div className="hidden md:block overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="min-w-full divide-y divide-slate-800 text-sm">
            <thead className="bg-slate-900/40">
              <tr>
                <th className="py-4 px-4 text-left text-slate-300 font-semibold">Features</th>
                <th className="py-4 px-4 text-center text-slate-100 font-semibold">Free</th>
                <th className="py-4 px-4 text-center text-sky-100 font-semibold">Basic</th>
                <th className="py-4 px-4 text-center text-purple-100 font-semibold">Pro</th>
                <th className="py-4 px-4 text-center text-amber-100 font-semibold">Enterprise</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Price</td>
                <td className="py-3 px-4 text-center text-slate-200">Free</td>
                <td className="py-3 px-4 text-center text-slate-200">
                  {hasPromotion('Basic') ? (
                    <>
                      <span className="line-through text-slate-500">20 KWD</span> <span className="text-pink-400 font-bold">10 KWD</span> / mo 🎉
                      <br />
                      <span className="text-xs text-pink-400">6 months promo, then 20 KWD</span>
                    </>
                  ) : (
                    <>20 KWD / mo<br /><span className="text-xs text-slate-400">200 KWD / yr</span></>
                  )}
                </td>
                <td className="py-3 px-4 text-center text-slate-200">
                  {hasPromotion('Pro') ? (
                    <>
                      <span className="line-through text-slate-500">60 KWD</span> <span className="text-pink-400 font-bold">30 KWD</span> / mo 🎉
                      <br />
                      <span className="text-xs text-pink-400">6 months promo, then 60 KWD</span>
                    </>
                  ) : (
                    <>60 KWD / mo<br /><span className="text-xs text-slate-400">600 KWD / yr</span></>
                  )}
                </td>
                <td className="py-3 px-4 text-center text-slate-200">Custom pricing<br /><span className="text-xs text-slate-400">Contact sales</span></td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Commission Fees</td>
                <td className="py-3 px-4 text-center text-slate-200">15%</td>
                <td className="py-3 px-4 text-center text-slate-200">12.5%</td>
                <td className="py-3 px-4 text-center text-slate-200">10%</td>
                <td className="py-3 px-4 text-center text-slate-200">5%</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Online Booking System</td>
                <td className="py-3 px-4 text-center text-emerald-300">Fully functional</td>
                <td className="py-3 px-4 text-center text-emerald-300">Fully functional</td>
                <td className="py-3 px-4 text-center text-emerald-300">Fully functional</td>
                <td className="py-3 px-4 text-center text-emerald-300">Fully functional</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Marketing</td>
                <td className="py-3 px-4 text-center text-slate-200">Basic</td>
                <td className="py-3 px-4 text-center text-slate-200">Basic</td>
                <td className="py-3 px-4 text-center text-slate-200">Pro package</td>
                <td className="py-3 px-4 text-center text-slate-200">Ultimate + first page + top list</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Staff Members</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 5</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 8</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 12</td>
                <td className="py-3 px-4 text-center text-slate-200">25 – 100</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Services</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 35</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 60</td>
                <td className="py-3 px-4 text-center text-slate-200">Up to 75</td>
                <td className="py-3 px-4 text-center text-slate-200">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Branches</td>
                <td className="py-3 px-4 text-center text-slate-200">1 branch</td>
                <td className="py-3 px-4 text-center text-slate-200">3 branches</td>
                <td className="py-3 px-4 text-center text-slate-200">6 branches</td>
                <td className="py-3 px-4 text-center text-slate-200">10 – 20 branches</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Bookings per month</td>
                <td className="py-3 px-4 text-center text-slate-200">Unlimited</td>
                <td className="py-3 px-4 text-center text-slate-200">Unlimited</td>
                <td className="py-3 px-4 text-center text-slate-200">Unlimited</td>
                <td className="py-3 px-4 text-center text-slate-200">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Secure Online Doctor–User Appointments</td>
                <td className="py-3 px-4 text-center text-slate-500">—</td>
                <td className="py-3 px-4 text-center text-slate-500">—</td>
                <td className="py-3 px-4 text-center text-slate-500">—</td>
                <td className="py-3 px-4 text-center text-emerald-300 font-medium">✅ Included</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Bookkeeping</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
              </tr>
              <tr>
                <td className="py-3 px-4 text-slate-100 font-medium">Free Reminders (providers & users)</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
                <td className="py-3 px-4 text-center text-emerald-300">✅</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Back to Home */}
        <div className="mt-12 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sky-400 hover:text-sky-300 text-sm font-medium"
          >
            ← Back to Home
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProviderPricingPage;
